import React, { useContext, useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import NeoButton from "./NeoButton";
import { useRoomsStore } from "../@shared/store/useRoomsStore";
import { useNavigate } from "react-router-dom";
import { Remove as RemoveIcon, Add as AddIcon } from "@mui/icons-material";
import { useAuthStore } from "../@shared/store/useAuthStore";
import { MainContext } from "../App";
import ModalComponent from "./ModalComponent";
import {
  getRoomSnapshotRTDB,
  updateRoomSettingsRTDB,
} from "../@shared/utils/roomFunctions";
import { motion } from "framer-motion";
import { GAME_MODES } from "../@shared/game/modes";
import { dialogPaperSx } from "../@shared/ui/tokens";

const MotionBox = motion(Box);

const screenHeight = window.innerHeight;

const modalHeight = screenHeight * 0.6;
const modalWidth = modalHeight * (4 / 5);

const wagerAmounts = [0, 50, 100, 200, 500, 1000];
const ethWagerAmounts = [0, 0.00032, 0.0016, 0.0032, 0.0079];
const DEFAULT_RANKED_TOKEN_WAGER =
  wagerAmounts.find((amount) => amount > 0) ?? 50;
const DEFAULT_RANKED_ETH_WAGER =
  ethWagerAmounts.find((amount) => amount > 0) ?? 0.00032;

const getMinimumRankedWagerAmount = (currency = "tokens") =>
  currency === "eth" ? DEFAULT_RANKED_ETH_WAGER : DEFAULT_RANKED_TOKEN_WAGER;



const StartRoomModal = ({
  visible,
  onClose,
  editRoomId = null,
  onCreateRoom = null,
  forcePrivate = false,
  initialMode = null,
  initialTeamCount = null,
  lockModeSelection = false,
  lockTeamCountSelection = false,
  titleText = null,
  createButtonText = null,
}) => {
  const { userData } = useAuthStore();
  const { Theme, setScreenLoading, deviceType, setMessage, setError } =
    useContext(MainContext);
  const [mode, setMode] = useState(GAME_MODES.ROYALE);
  const [teamCount, setTeamCount] = useState(2); // 👈 NEW: only used when mode === TEAM
  const [amountOfPlayers, setAmountOfPlayers] = useState(6);
  const [isRanked, setIsRanked] = useState(true);
  const [botFill, setBotFill] = useState(true);
  const [isPublic, setIsPublic] = useState(true);
  const [timedTurns, setTimedTurns] = useState(true);
  const [turnTime, setTurnTime] = useState(30);
  const [submitting, setSubmitting] = useState(false);
  const [wagerAmount, setWagerAmount] = useState(DEFAULT_RANKED_TOKEN_WAGER);
  const [wagerCurrency, setWagerCurrency] = useState("tokens"); // "tokens" | "eth"
  const [lowTokenPromptOpen, setLowTokenPromptOpen] = useState(false);


  const activeWagerAmounts =
    wagerCurrency === "eth" ? ethWagerAmounts : wagerAmounts;
  const minimumRankedWagerAmount = getMinimumRankedWagerAmount(wagerCurrency);


  const scrollRef = useRef(null);
  const submitInFlightRef = useRef(false);

  const scrollIfNeeded = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
    if (!atBottom) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }
  };

  const createAndSetRoom = useRoomsStore((s) => s.createAndSetRoom);
  const navigate = useNavigate();

  const getTeamLabel = () => {
    if (teamCount === 3) return "3 Teams";
    return "2 Teams";
  };

  useEffect(() => {
    if (mode !== GAME_MODES.ROYALE && isRanked) {
      setIsRanked(false);
    }
  }, [isRanked, mode]);

  useEffect(() => {
    const normalizedWagerAmount = activeWagerAmounts.includes(wagerAmount)
      ? wagerAmount
      : activeWagerAmounts[0];

    if (mode !== GAME_MODES.ROYALE) {
      if (wagerAmount !== 0) {
        setWagerAmount(0);
      }
      return;
    }

    if (isRanked) {
      const nextRankedWagerAmount = Math.max(
        normalizedWagerAmount,
        minimumRankedWagerAmount,
      );
      if (wagerAmount !== nextRankedWagerAmount) {
        setWagerAmount(nextRankedWagerAmount);
      }
      return;
    }

    if (wagerAmount !== normalizedWagerAmount) {
      setWagerAmount(normalizedWagerAmount);
    }
  }, [
    activeWagerAmounts,
    isRanked,
    minimumRankedWagerAmount,
    mode,
    wagerAmount,
  ]);

  // 🔒 If 3-team mode but players drop below 6, auto-downgrade to 2 teams
  useEffect(() => {
    if (mode === GAME_MODES.TEAMS && teamCount === 3 && amountOfPlayers < 6) {
      setTeamCount(2);
    }
  }, [amountOfPlayers, mode, teamCount]);

  // Prefill when editing a room
  useEffect(() => {
    if (!editRoomId) return;

    (async () => {
      const data = await getRoomSnapshotRTDB(editRoomId);
      if (!data) return;

      setAmountOfPlayers(data.amountOfPlayers ?? 6);
      setIsRanked(Boolean(data.ranked));
      setBotFill(Boolean(data.botFill));
      setIsPublic(Boolean(data.public));
      setTimedTurns(Boolean(data.timedTurns));
      setTurnTime(Number(data.turnTime ?? 30));
      setMode(data.mode || GAME_MODES.ROYALE);
      setTeamCount(data.teamCount || 2); // 👈 NEW (safe: only meaningful in TEAM mode)
      const nextWagerCurrency = data?.wager?.currency || "tokens";
      setWagerCurrency(nextWagerCurrency);
      setWagerAmount(
        Number(
          data?.wager?.amount ??
            (Boolean(data?.ranked)
              ? getMinimumRankedWagerAmount(nextWagerCurrency)
              : 0),
        ),
      );
    })();
  }, [editRoomId]);

  useEffect(() => {
    if (forcePrivate) {
      setIsPublic(false);
    }
  }, [forcePrivate, visible]);

  useEffect(() => {
    if (!visible || editRoomId) return;

    if (initialMode) {
      setMode(initialMode);
      if (initialMode !== GAME_MODES.ROYALE) {
        setIsRanked(false);
      }
    }

    if (
      initialMode === GAME_MODES.TEAMS &&
      (initialTeamCount === 2 || initialTeamCount === 3)
    ) {
      setTeamCount(initialTeamCount);
    }

    if (initialMode === GAME_MODES.PVE) {
      setBotFill(true);
    }
  }, [visible, editRoomId, initialMode, initialTeamCount]);

  useEffect(() => {
    if (visible) return;
    setSubmitting(false);
    setLowTokenPromptOpen(false);
    submitInFlightRef.current = false;
  }, [visible]);

  const validateTeams = () => {
    if (mode === GAME_MODES.TEAMS && teamCount === 3 && amountOfPlayers < 6) {
      setMessage("3-team mode requires at least 6 players.");
      setError(true);
      return false;
    }
    return true;
  };

  const handleCreate = async () => {
    if (submitInFlightRef.current) return;

    console.log("Currently Creating room with:", {
      amountOfPlayers,
      botFill,
      public: isPublic,
      timedTurns,
      turnTime,
      ranked: isRanked,
      mode,
      teamCount,
    });

    if (!validateTeams()) return;

    // const creatorBalance = Number(userData?.infinityTokens ?? 0);
    // const effectiveWagerAmount = mode === GAME_MODES.ROYALE ? wagerAmount : 0;

    // if (creatorBalance < effectiveWagerAmount) {
    //   setMessage(
    //     `You need at least ${effectiveWagerAmount} Infinity Tokens to create this wagered room.`
    //   );
    //   setError(true);
    //   return;
    // }

    const effectiveWagerAmount = mode === GAME_MODES.ROYALE ? wagerAmount : 0;
    const normalizedWagerAmount = isRanked
      ? Math.max(Number(effectiveWagerAmount || 0), minimumRankedWagerAmount)
      : Number(effectiveWagerAmount || 0);

    const creatorTokenBalance = Number(userData?.infinityTokens ?? 0);
    // assuming your in-game ETH is stored like this (matches your BattleWallet work)
    const creatorEthBalance = Number(userData?.battleWalletBalance ?? 0);

    const creatorBalance =
      wagerCurrency === "eth" ? creatorEthBalance : creatorTokenBalance;

    if (creatorBalance < normalizedWagerAmount) {
      if (
        wagerCurrency === "tokens" &&
        creatorTokenBalance < DEFAULT_RANKED_TOKEN_WAGER
      ) {
        setLowTokenPromptOpen(true);
      }
      const label = wagerCurrency === "eth" ? "ETH" : "Infinity Tokens";
      setMessage(`You need at least ${normalizedWagerAmount} ${label} to create this wagered room.`);
      setError(true);
      return;
    }


    const player = {
      id: userData.id,
      username: userData.username,
      avatar: userData.avatar,
      orbAvatar: userData.orbAvatar || null,
      podium: userData.podium || null,
      email: userData.email,
      record: userData.record || { wins: 0, losses: 0 },
      level: userData.level || 1,
      partyId: userData.partyId || null,
      xp: userData.xp || 0,
      sticker: userData.sticker || null,
      selectedCardSlap: Number(userData?.selectedCardSlap || 1),
      selectedTradeAnimation: Number(
        userData?.selectedTradeAnimation || userData?.selectedTradAnimation || 1
      ),
      isHost: true,
    };

    const accessCode = Math.floor(1000000 + Math.random() * 9000000);

    const isWagered = normalizedWagerAmount !== 0;

    const roomSettings = {
      accessCode,
      amountOfPlayers,
      botFill,
      players: {
        [player.id]: {
          ...player,
          ready: false,
          character: null,
          isBot: false,
        },
      },
      public: forcePrivate ? false : isPublic,
      ranked: mode === GAME_MODES.ROYALE && isRanked,
      mode,
      teamCount: mode === GAME_MODES.TEAMS ? teamCount : null, // 👈 NEW
      timedTurns,
      turnTime,
      // wager: { enabled: isWagered, amount: effectiveWagerAmount },
      wager: {
        enabled: isWagered,
        amount: normalizedWagerAmount,
        currency: wagerCurrency, // ✅ "tokens" | "eth"
      },

    };

    submitInFlightRef.current = true;
    setSubmitting(true);

    try {
      if (typeof onCreateRoom === "function") {
        await onCreateRoom({
          accessCode,
          lobbyPath: `/lobby/${accessCode}`,
          lobbyUrl:
            typeof window !== "undefined"
              ? `${window.location.origin}/lobby/${accessCode}`
              : `/lobby/${accessCode}`,
          roomSettings,
        });
        onClose?.();
        return;
      }

      await createAndSetRoom(userData?.id, roomSettings);

      onClose();
      setScreenLoading(true);
      const timeout = setTimeout(() => {
        navigate(`/lobby/${accessCode}`);
      }, 500);

      return () => clearTimeout(timeout);
    } catch (err) {
      console.error("Error creating room:", err);
      setMessage(err?.message || "Failed to create room.");
      setError(true);
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  const handleSave = async () => {
    if (!editRoomId || submitInFlightRef.current) return;

    submitInFlightRef.current = true;
    setSubmitting(true);

    try {
      const data = await getRoomSnapshotRTDB(editRoomId);
      if (!data) return;
      const currentCount = Object.values(data.players || {}).length;

      if (amountOfPlayers < currentCount) {
        setMessage(
          `There are currently ${currentCount} players in the room. Increase "Players" to at least ${currentCount} or ask someone to leave first.`
        );
        setError(true);
        return;
      }

      if (!validateTeams()) return;

      const effectiveWagerAmount =
        mode === GAME_MODES.ROYALE
          ? isRanked
            ? Math.max(Number(wagerAmount || 0), minimumRankedWagerAmount)
            : Number(wagerAmount || 0)
          : 0;

      await updateRoomSettingsRTDB(editRoomId, {
        amountOfPlayers,
        botFill,
        public: isPublic,
        ranked: mode === GAME_MODES.ROYALE && isRanked,
        mode,
        teamCount: mode === GAME_MODES.TEAMS ? teamCount : null, // 👈 NEW
        timedTurns,
        turnTime: turnTime,
        wager: {
          enabled: effectiveWagerAmount > 0,
          amount: effectiveWagerAmount,
          currency: wagerCurrency,
        },
      });

      onClose?.();
    } catch (err) {
      console.error("Error saving room settings:", err);
      setMessage(err?.message || "Failed to save room settings.");
      setError(true);
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  const getModeLabel = () => {
    if (mode === GAME_MODES.PVE) return "PvE";
    if (mode === GAME_MODES.TEAMS) {
      return "Teams";
    }
    return "Royale";
  };

  return (
    <ModalComponent
      aspectRatio={deviceType === "desktop" ? "5/7" : "5/8"}
      ingnorAspect={false}
      md={5}
      lg={3.5}
      modalOpen={visible}
      setModalOpen={
        submitting
          ? () => {}
          : () => {
              setLowTokenPromptOpen(false);
              onClose?.();
            }
      }
    >
      <Typography
        variant="h6"
        sx={{
          color: "#fff",
          fontWeight: "bold",
          mb: 2,
          textAlign: "center",
          padding: "10px",
          borderRadius: "5px",
          backgroundColor: "#00000080",
          width: "100%",
          height: "fit-content",
        }}
      >
        {titleText || (editRoomId !== null ? "Edit Room" : "Create Room")}
      </Typography>

      <Box
        ref={scrollRef}
        style={{
          height: "calc(100% - 100px)",
          width: "96%",
          overflowY: "scroll",
          overflowX: "hidden",
          scrollbarWidth: "none",
          paddingRight: 8,
        }}
      >
        {/* ROOM TYPE */}
        <Box
          sx={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.1)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
            py: 1,
            px: 1.5,
            borderRadius: "16px",
            boxSizing: "border-box",
            width: "98%",
            display: "flex",
            flexFlow: "row",
            alignItems: "center",
            justifyContent: "space-between",
            containerType: "inline-size",
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "0.7rem",
              letterSpacing: "0.18em",
              color: "rgba(214,204,255,0.65)",
            }}
          >
            ROOM TYPE
          </Typography>

          <Box
            sx={{
              padding: "1.5%",
              backgroundColor: "rgba(124,92,255,0.12)",
                  border: "1px solid rgba(157,133,255,0.25)",
              borderRadius: "8px",
              display: "flex",
              flexFlow: "row",
              alignItems: "center",
              justifyContent: "space-evenly",
              width: "fit-content",
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "5cqw", md: "3cqw" },
                color: isPublic ? "#fff" : Theme.palette.secondary.main,
                fontWeight: "bold",
                textShadow: ` -1px -1px 0 ${Theme.palette.tirciary.dark}, 1px -1px 0 ${Theme.palette.tirciary.dark}, -1px  1px 0 ${Theme.palette.tirciary.dark}, 1px  1px 0 ${Theme.palette.tirciary.dark}`,
              }}
            >
              Private
            </Typography>
            <Switch
              checked={isPublic}
              disabled={forcePrivate}
              onChange={(e) => {
                setIsPublic(e.target.checked);
              }}
            />
            <Typography
              sx={{
                fontSize: { xs: "5cqw", md: "3cqw" },
                color: !isPublic ? "#fff" : Theme.palette.primary.main,
                fontWeight: "bold",
                textShadow: ` -1px -1px 0 ${Theme.palette.tirciary.dark}, 1px -1px 0 ${Theme.palette.tirciary.dark}, -1px  1px 0 ${Theme.palette.tirciary.dark}, 1px  1px 0 ${Theme.palette.tirciary.dark}`,
              }}
            >
              Public
            </Typography>
          </Box>
        </Box>

        {/* SETTINGS */}
        <Box
          sx={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.1)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
            py: 1,
            px: 1.5,
            borderRadius: "16px",
            boxSizing: "border-box",
            width: "98%",
            display: "flex",
            flexFlow: "column",
            alignItems: "space-between",
            justifyContent: "center",
            containerType: "inline-size",
            mb: 2,
          }}
        >
          <Box sx={{ width: "100%" }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "0.7rem",
                letterSpacing: "0.18em",
                color: "rgba(214,204,255,0.65)",
              }}
            >
              SETTINGS
            </Typography>
          </Box>

          {/* ROW 1: Players / Ranked / Timer */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexFlow: "row",
              justifyContent: "space-evenly",
            }}
          >
            {/* PLAYERS */}
            <MotionBox
              whileTap={{ scale: 0.98 }}
              sx={{
                width: "28%",
                padding: "5px",
                display: "flex",
                flexFlow: "column",
                justifyContent: "center",
                alignItems: "center",
                containerType: "inline-size",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  padding: "5px",
                  display: "flex",
                  flexFlow: "column",
                  backgroundColor: "rgba(124,92,255,0.12)",
                  border: "1px solid rgba(157,133,255,0.25)",
                  borderRadius: "8px",
                  minHeight: { xs: "80px", md: "100px" },
                  justifyContent: "center",
                  alignItems: "center",
                  mb: "5px",
                }}
              >
                <img
                  style={{ width: "50%", height: "auto" }}
                  src={require("../@shared/assets/icons/avatar_icon.png")}
                  alt=""
                />
                <Box sx={styles.incrementRow}>
                  <RemoveIcon
                    sx={{ fontSize: { xs: "20px", md: "30px" }, color: "#fff" }}
                    onClick={() =>
                      setAmountOfPlayers((prev) => Math.max(2, prev - 1))
                    }
                  />

                  <Typography
                    sx={{
                      color: "#fff",
                      mx: 1,
                      fontSize: { xs: "30cqw", md: "12cqw" },
                      fontWeight: "bold",
                    }}
                  >
                    {amountOfPlayers}
                  </Typography>

                  <AddIcon
                    sx={{ fontSize: { xs: "20px", md: "30px" }, color: "#fff" }}
                    onClick={() =>
                      setAmountOfPlayers((prev) => Math.min(12, prev + 1))
                    }
                  />
                </Box>
              </Box>
              <Typography
                sx={{
                  width: "100%",
                  fontWeight: "bold",
                  textAlign: "center",
                  fontSize: { xs: "20cqw", md: "12cqw" },
                  color: "#fff",
                }}
              >
                Players
              </Typography>
            </MotionBox>

            {/* RANKED */}
            <MotionBox
              whileTap={{ scale: 0.98 }}
              sx={{
                width: "28%",
                padding: "5px",
                display: "flex",
                flexFlow: "column",
                justifyContent: "center",
                alignItems: "center",
                containerType: "inline-size",
              }}
              onClick={() => {
                if (mode !== GAME_MODES.ROYALE) {
                  setMessage("Ranked is only available in Royale mode.");
                  setError(true);
                  return;
                }
                setIsRanked(!isRanked);
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  padding: "5px",
                  display: "flex",
                  flexFlow: "column",
                  backgroundColor: "rgba(124,92,255,0.12)",
                  border: "1px solid rgba(157,133,255,0.25)",
                  borderRadius: "8px",
                  minHeight: { xs: "80px", md: "100px" },
                  justifyContent: "center",
                  alignItems: "center",
                  mb: "5px",
                }}
              >
                <img
                  style={{ width: "50%", height: "auto" }}
                  src={
                    isRanked
                      ? require("../@shared/assets/icons/clans_icon.png")
                      : require("../@shared/assets/icons/flag_icon.png")
                  }
                  alt=""
                />
                <Typography
                  sx={{
                    width: "100%",
                    fontWeight: "bold",
                    textAlign: "center",
                    fontSize: { xs: "20cqw", md: "12cqw" },
                    color: "#fff",
                  }}
                >
                  {isRanked ? "Ranked" : "Exhibition"}
                </Typography>
              </Box>
              <Typography
                sx={{
                  width: "100%",
                  fontWeight: "bold",
                  textAlign: "center",
                  fontSize: { xs: "20cqw", md: "12cqw" },
                  color: "#fff",
                }}
              >
                Record
              </Typography>
            </MotionBox>

            {/* TIMER */}
            <MotionBox
              whileTap={{ scale: 0.98 }}
              sx={{
                width: "28%",
                padding: "5px",
                display: "flex",
                flexFlow: "column",
                justifyContent: "center",
                alignItems: "center",
                containerType: "inline-size",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  padding: "5px",
                  display: "flex",
                  flexFlow: "column",
                  backgroundColor: "rgba(124,92,255,0.12)",
                  border: "1px solid rgba(157,133,255,0.25)",
                  borderRadius: "8px",
                  minHeight: { xs: "80px", md: "100px" },
                  justifyContent: "center",
                  alignItems: "center",
                  mb: "5px",
                }}
              >
                <img
                  style={{ width: "50%", height: "auto" }}
                  src={require("../@shared/assets/icons/time_icon.png")}
                  alt=""
                />
                <Box sx={styles.incrementRow}>
                  <RemoveIcon
                    sx={{ fontSize: { xs: "20px", md: "30px" }, color: "#fff" }}
                    onClick={() => {
                      setTurnTime((prev) => Math.max(20, prev - 10));
                      scrollIfNeeded();
                    }}
                  />

                  <Typography
                    sx={{
                      color: "#fff",
                      mx: 1,
                      fontSize: { xs: "30cqw", md: "12cqw" },
                      fontWeight: "bold",
                    }}
                  >
                    {turnTime}
                  </Typography>

                  <AddIcon
                    sx={{ fontSize: { xs: "20px", md: "30px" }, color: "#fff" }}
                    onClick={() => {
                      setTurnTime((prev) => Math.min(60, prev + 10));
                      scrollIfNeeded();
                    }}
                  />
                </Box>
              </Box>
              <Typography
                sx={{
                  width: "100%",
                  fontWeight: "bold",
                  textAlign: "center",
                  fontSize: { xs: "20cqw", md: "12cqw" },
                  color: "#fff",
                }}
              >
                Timer
              </Typography>
            </MotionBox>
          </Box>

          {/* ROW 2: Bots / Mode (with Teams) */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexFlow: "row",
              justifyContent: "space-evenly",
            }}
          >
            {/* BOTS */}
            <MotionBox
              whileTap={{ scale: 0.98 }}
              sx={{
                width: "28%",
                padding: "5px",
                display: "flex",
                flexFlow: "column",
                justifyContent: "center",
                alignItems: "center",
                containerType: "inline-size",
              }}
              onClick={() => {
                if (lockModeSelection && mode === GAME_MODES.PVE && botFill) {
                  setMessage("Bots are required for this event's locked PvE mode.");
                  setError(true);
                  return;
                }
                // If turning botFill off and we were in PvE, force back to Royale
                if (botFill && mode === GAME_MODES.PVE) {
                  setMode(GAME_MODES.ROYALE);
                }
                setBotFill(!botFill);
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  padding: "5px",
                  display: "flex",
                  flexFlow: "column",
                  backgroundColor: "rgba(124,92,255,0.12)",
                  border: "1px solid rgba(157,133,255,0.25)",
                  borderRadius: "8px",
                  minHeight: { xs: "80px", md: "100px" },
                  justifyContent: "center",
                  alignItems: "center",
                  mb: "5px",
                }}
              >
                <img
                  style={{ width: "50%", height: "auto" }}
                  src={
                    botFill
                      ? require("../@shared/assets/icons/self.png")
                      : require("../@shared/assets/icons/target.png")
                  }
                  alt=""
                />
                <Typography
                  sx={{
                    width: "100%",
                    fontWeight: "bold",
                    textAlign: "center",
                    fontSize: { xs: "20cqw", md: "12cqw" },
                    color: "#fff",
                  }}
                >
                  {botFill ? "On" : "Off"}
                </Typography>
              </Box>
              <Typography
                sx={{
                  width: "100%",
                  fontWeight: "bold",
                  textAlign: "center",
                  fontSize: { xs: "20cqw", md: "12cqw" },
                  color: "#fff",
                }}
              >
                Bots
              </Typography>
            </MotionBox>

            {/* MODE (Royale / PvE / Teams) */}
            <MotionBox
              whileTap={{ scale: 0.98 }}
              sx={{
                width: "28%",
                padding: "5px",
                display: "flex",
                flexFlow: "column",
                justifyContent: "center",
                alignItems: "center",
                containerType: "inline-size",
                opacity: lockModeSelection ? 0.72 : 1,
                cursor: lockModeSelection ? "not-allowed" : "pointer",
              }}
              onClick={() => {
                if (lockModeSelection) return;
                // Cycle: Royale → PvE (if bots) → Team → Royale
                if (mode === GAME_MODES.ROYALE) {
                  if (botFill) {
                    setMode(GAME_MODES.PVE);
                  } else {
                    setMode(GAME_MODES.TEAMS);
                  }
                  return;
                }

                if (mode === GAME_MODES.PVE) {
                  setMode(GAME_MODES.TEAMS);
                  return;
                }

                if (mode === GAME_MODES.TEAMS) {
                  setMode(GAME_MODES.ROYALE);
                }
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  padding: "5px",
                  display: "flex",
                  flexFlow: "column",
                  backgroundColor: "rgba(124,92,255,0.12)",
                  border: "1px solid rgba(157,133,255,0.25)",
                  borderRadius: "8px",
                  minHeight: { xs: "80px", md: "100px" },
                  justifyContent: "center",
                  alignItems: "center",
                  mb: "5px",
                }}
              >
                <img
                  style={{ width: "50%", height: "auto" }}
                  src={
                    mode === GAME_MODES.PVE
                      ? require("../@shared/assets/icons/both.png")
                      : mode === GAME_MODES.TEAMS
                        ? require("../@shared/assets/icons/nfteams.png")
                        : require("../@shared/assets/icons/swords_icon2.png")
                  }
                  alt=""
                />
                <Typography
                  sx={{
                    width: "100%",
                    fontWeight: "bold",
                    textAlign: "center",
                    fontSize: { xs: "20cqw", md: "12cqw" },
                    color: "#fff",
                  }}
                >
                  {getModeLabel()}
                </Typography>
              </Box>
              <Typography
                sx={{
                  width: "100%",
                  fontWeight: "bold",
                  textAlign: "center",
                  fontSize: { xs: "20cqw", md: "12cqw" },
                  color: "#fff",
                }}
              >
                Mode
              </Typography>
            </MotionBox>

            {/* TEAMS (only active in TEAM mode) */}
            {mode === GAME_MODES.TEAMS && (
              <MotionBox
                whileTap={mode === GAME_MODES.TEAMS ? { scale: 0.98 } : {}}
                sx={{
                  width: "28%",
                  padding: "5px",
                  display: "flex",
                  flexFlow: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  containerType: "inline-size",
                  opacity:
                    mode === GAME_MODES.TEAMS
                      ? lockTeamCountSelection
                        ? 0.72
                        : 1
                      : 0.4, // dim when not in team mode
                  cursor:
                    mode === GAME_MODES.TEAMS && lockTeamCountSelection
                      ? "not-allowed"
                      : "pointer",
                }}
                onClick={() => {
                  if (mode !== GAME_MODES.TEAMS) return; // only works in team mode
                  if (lockTeamCountSelection) return;

                  if (teamCount === 2) {
                    if (amountOfPlayers < 6) {
                      setMessage("3 teams requires at least 6 players.");
                      setError(true);
                      return;
                    }
                    setTeamCount(3);
                  } else {
                    setTeamCount(2);
                  }
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    padding: "5px",
                    display: "flex",
                    flexFlow: "column",
                    backgroundColor: "rgba(124,92,255,0.12)",
                  border: "1px solid rgba(157,133,255,0.25)",
                    borderRadius: "8px",
                    minHeight: { xs: "80px", md: "100px" },
                    justifyContent: "center",
                    alignItems: "center",
                    mb: "5px",
                  }}
                >
                  <img
                    style={{ width: "50%", height: "auto" }}
                    src={require("../@shared/assets/icons/clans_icon.png")} // or a new teams icon
                    alt=""
                  />
                  <Typography
                    sx={{
                      width: "100%",
                      fontWeight: "bold",
                      textAlign: "center",
                      fontSize: { xs: "20cqw", md: "12cqw" },
                      color: "#fff",
                    }}
                  >
                    {getTeamLabel()}
                  </Typography>

                  {mode === GAME_MODES.TEAMS && (
                    <Typography
                      sx={{
                        width: "100%",
                        textAlign: "center",
                        fontSize: { xs: "12cqw", md: "7cqw" },
                        color: "#ddd",
                      }}
                    >
                      {teamCount === 3
                        ? "Requires 6+ players"
                        : "Tap to toggle"}
                    </Typography>
                  )}
                </Box>

                <Typography
                  sx={{
                    width: "100%",
                    fontWeight: "bold",
                    textAlign: "center",
                    fontSize: { xs: "20cqw", md: "12cqw" },
                    color: "#fff",
                  }}
                >
                  Teams
                </Typography>
              </MotionBox>
            )}
          </Box>
        </Box>

        {/* WAGER */}
        {!editRoomId && mode === GAME_MODES.ROYALE && (
          <Box
            sx={{
              backgroundColor: Theme.palette.tirciary.main,
              py: "1%",
              px: "10px",
              borderRadius: "8px",
              border: `2px solid ${Theme.palette.tirciary.dark} 2px solid ${Theme.palette.tirciary.dark} 0 0`,
              width: "98%",
              display: "flex",
              flexFlow: "row",
              alignItems: "center",
              justifyContent: "space-between",
              containerType: "inline-size",
              mb: 2,
            }}
          >
            <Box
              sx={{
                width: "60%",
                display: "flex",
                gap: 1,
                mb: 1,
              }}
            >
              <Box
                onClick={() => setWagerCurrency("tokens")}
                sx={{
                  flex: 1,
                  cursor: "pointer",
                  borderRadius: "8px",
                  backgroundColor:
                    wagerCurrency === "tokens" ? "#00000095" : "#00000055",
                  border:
                    wagerCurrency === "tokens"
                      ? `2px solid ${Theme.palette.primary.main}`
                      : "2px solid transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: 'relative',
                  top: '4px',
                  py: 0.8,
                }}
              >
                <img
                  style={{ width: 22, height: 22, marginRight: 8 }}
                  src={require("../@shared/assets/infift_token.png")}
                  alt=""
                />
                <Typography sx={{ color: "#fff", fontWeight: "bold", fontSize: { xs: "3.5cqw", md: "2cqw" } }}>
                  Tokens
                </Typography>
              </Box>

              <Box
                onClick={() => setWagerCurrency("eth")}
                sx={{
                  flex: 1,
                  cursor: "pointer",
                  borderRadius: "8px",
                  backgroundColor: wagerCurrency === "eth" ? "#00000095" : "#00000055",
                  border:
                    wagerCurrency === "eth"
                      ? `2px solid ${Theme.palette.primary.main}`
                      : "2px solid transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: '5%',
                  position: 'relative',
                  top: '4px'
                  // py: 0.8,
                }}
              >
                <img
                  style={{ width: 22, height: 22, marginRight: 8 }}
                  src={require("../@shared/assets/icons/eth.png")}  // ✅ put your eth image here
                  alt=""
                />
                <Typography sx={{ color: "#fff", fontWeight: "bold", fontSize: { xs: "3.5cqw", md: "2cqw" } }}>
                  ETH
                </Typography>
              </Box>
            </Box>


            {/* <Typography
              sx={{
                fontWeight: "bold",
                fontSize: { xs: "5cqw", md: "4cqw" },
                color: "#fff",
              }}
            >
              {wagerAmount === 0 && "NO "} WAGER
            </Typography> */}

            <Box
              sx={{
                padding: "1.5%",
                backgroundColor: "rgba(124,92,255,0.12)",
                  border: "1px solid rgba(157,133,255,0.25)",
                borderRadius: "8px",
                display: "flex",
                flexFlow: "row",
                alignItems: "center",
                justifyContent: "space-evenly",
                width: "60%",
              }}
            >
              <RemoveIcon
                sx={{ fontSize: { xs: "20px", md: "30px" }, color: "#fff" }}
                onClick={() => {
                  if (wagerAmount === activeWagerAmounts[0]) return;
                  let currentIdx = activeWagerAmounts.findIndex((n) => n === wagerAmount);
                  setWagerAmount(activeWagerAmounts[currentIdx - 1]);


                }}
              />
              <img
                style={{ width: "15%", height: "auto" }}
                src={
                  wagerCurrency === "eth"
                    ? require("../@shared/assets/icons/eth.png")
                    : require("../@shared/assets/infift_token.png")
                }
                alt=""
              />

              <Typography
                sx={{
                  color: "#fff",
                  mx: 1,
                  fontSize: { xs: "12.5cqw", md: "5cqw" },
                  fontWeight: "bold",
                }}
              >
                {wagerAmount}
              </Typography>
              <AddIcon
                sx={{ fontSize: { xs: "20px", md: "30px" }, color: "#fff" }}
                onClick={() => {
                  if (wagerAmount === activeWagerAmounts[activeWagerAmounts.length - 1]) return;
                  let currentIdx = activeWagerAmounts.findIndex((n) => n === wagerAmount);
                  setWagerAmount(activeWagerAmounts[currentIdx + 1]);
                }}
              />
            </Box>
          </Box>
        )}

        <Box sx={styles.buttonRow}>
          <NeoButton
            text={
              submitting
                ? editRoomId
                  ? "Saving..."
                  : "Starting..."
                : editRoomId
                  ? "Save Changes"
                  : createButtonText || "Start"
            }
            color={Theme.palette.primary.main}
            width={98}
            onClick={editRoomId ? handleSave : handleCreate}
            disabled={submitting}
          />
        </Box>
      </Box>


      <Dialog
        open={lowTokenPromptOpen}
        onClose={() => setLowTokenPromptOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            ...dialogPaperSx,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "#ffd24d",
            fontWeight: 900,
            textAlign: "center",
            borderBottom: "2px solid #120a3a",
          }}
        >
          Need More Infinity Tokens
        </DialogTitle>
        <DialogContent
          sx={{
            pt: 2,
            background:
              "radial-gradient(circle at top, #3b1f8a33 0%, transparent 55%)",
          }}
        >
          <Typography
            sx={{
              color: "#e2d5ff",
              fontWeight: 700,
              textAlign: "center",
            }}
          >
            Ranked token wagers now start at 50 Infinity Tokens. You currently
            have {Number(userData?.infinityTokens || 0)}.
          </Typography>
          <Typography
            sx={{
              color: "#bdbdde",
              fontSize: 13,
              textAlign: "center",
              mt: 1.5,
            }}
          >
            Would you like to head to the Store and purchase more tokens?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2,
            pb: 2,
            justifyContent: "center",
            gap: 1,
          }}
        >
          <NeoButton
            text="Not Now"
            color={Theme.palette.secondary.main}
            width={44}
            onClick={() => setLowTokenPromptOpen(false)}
          />
          <NeoButton
            text="Open Store"
            color={Theme.palette.primary.main}
            width={44}
            onClick={() => {
              setLowTokenPromptOpen(false);
              onClose?.();
              navigate("/store");
            }}
          />
        </DialogActions>
      </Dialog>
    </ModalComponent>
  );
};

const styles = {
  modalContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    p: 2,
    height: { xs: "70%", md: "60%" },
    width: { xs: "95%", md: "30%" },
    position: "absolute",
    outline: "none",
  },
  backgroundImage: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
  },
  modalContentWrapper: {
    width: modalWidth,
    height: modalHeight,
  },
  modalContent: {
    width: "100%",
    height: "100%",
    paddingTop: "18%",
    paddingLeft: "5%",
    paddingRight: "5%",
    paddingBottom: "12%",
    borderRadius: 16,
    elevation: 5,
    borderColor: "#fff",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  incrementRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  incrementButton: {
    width: 40,
  },
  toggleRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    my: 1,
    alignItems: "center",
  },
  buttonRow: {
    mt: 2,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
};

export default StartRoomModal;
